// Otto's chart knowledge is DATA-DRIVEN: the per-chart selectable options arrive in app_context (built by
// the dashboard from the SAME dropdown source the charts render — dashboard/chartOptionSources.js), so
// this module hardcodes NO option values. It only knows the two charts' display names + how to ground a
// requested change against whatever options the frontend sent. When the dashboard's options change, Otto
// follows automatically — nothing to maintain here.

// User-facing names in Otto's plain language (NOT the dashboard's formal chart titles, which read as
// "FCA N Year Annual CSP By Category" / "FCA N Year CSP" and confuse users). The annual chart shows spend
// BY YEAR (top-left); the CSP chart shows OVERALL cost (bottom-left).
export const CHART_LABELS = {
    annual_by_category: "Yearly Breakdown chart",
    csp: "Overall Cost chart"
};

// SCALAR (non-enum) chart properties: their value is a free number, VALIDATED as a positive integer rather
// than chosen from an option list (the grounding principle exempts thresholds/scalars). Top N is a universal
// display control both POC charts expose, so support is declared here (not a project-specific option catalog
// sourced from app_context). Everything else stays options-driven.
export const SCALAR_PROPERTIES = new Set(["top_n"]);
const SCALAR_SUPPORT = { top_n: ["annual_by_category", "csp"] };
const isPositiveInt = v => /^\d+$/.test(String(v == null ? "" : v).trim()) && Number(v) > 0;
export const isValidScalar = (property, value) => (property === "top_n" ? isPositiveInt(value) : false);
const scalarLabel = (property, value) => (property === "top_n" ? `Top ${String(value).trim()}` : String(value));

// app_context.charts[chart].options[property] = [{ value, label }] (from the dashboard).
export const optionsFor = (appContext, chart, property) => {
    const c = appContext && appContext.charts && appContext.charts[chart];
    return (c && c.options && c.options[property]) || [];
};

export const prettyValue = (appContext, chart, property, value) => {
    const opt = optionsFor(appContext, chart, property).find(o => o.value === value);
    return (opt && opt.label) || value;
};

export const allowedLabelsFor = (appContext, chart, property) => optionsFor(appContext, chart, property).map(o => o.label);

// Which charts expose `property` at all — or, when `value` is given, which charts offer THAT value.
// This is what powers "offer Both only if both charts actually support the value". Scalar properties
// (Top N) aren't option-driven: any supporting chart accepts any valid number.
export const chartsSupporting = (appContext, property, value) => {
    const chartKeys = Object.keys((appContext && appContext.charts) || {});
    if (SCALAR_PROPERTIES.has(property)) {
        if (value != null && !isValidScalar(property, value)) return [];
        return (SCALAR_SUPPORT[property] || []).filter(c => chartKeys.includes(c));
    }
    return chartKeys.filter(chart => {
        const opts = optionsFor(appContext, chart, property);
        return opts.length > 0 && (value == null || opts.some(o => o.value === value));
    });
};

// The friendly name of a chart, preferring one the PAGE supplied in app_context. CHART_LABELS covers the
// FCA dashboard's two charts, whose formal titles read as "FCA N Year Annual CSP By Category" and had to be
// renamed by hand; every other page (the Asset Dashboard's four charts) sends its own `label` alongside its
// options, so adding charts to Otto needs no edit here. Null when neither knows the chart — which is what
// groundChange uses to reject a chart that does not exist in this context.
export const chartLabelOf = (appContext, chart) => {
    const charts = (appContext && appContext.charts) || null;
    // When the page SENT a chart map, that map is the authority on which charts exist here. Falling back to
    // CHART_LABELS unconditionally would leak the FCA dashboard's two chart names into every page's
    // vocabulary, so naming "the overall cost chart" on the Asset Dashboard would look like a real chart
    // that merely lacks the property, instead of one that isn't on this page at all.
    if (charts && !charts[chart]) return null;
    const c = charts && charts[chart];
    return (c && c.label) || CHART_LABELS[chart] || null;
};

// Name a chart mid-sentence in the page's CURRENT language (the Yearly Breakdown chart keeps its live
// grouping suffix so it stays informative as the grouping changes, e.g. "the yearly breakdown chart (by Trade)").
// The chart's name mid-sentence WITHOUT the live-grouping suffix. Lowercase for the FCA dashboard's two
// charts because it reads as prose there ("Group the yearly breakdown chart by Trade"); every other page
// gets its own label verbatim. A CHOICE LIST wants the label title-cased instead — that is chartProposal's
// `chartChoiceName`, deliberately a different function.
export const chartBaseName = (appContext, chart) => {
    // Resolve the label FIRST: it is null when the page sent a chart map that does not contain this chart,
    // and in that case the two hand-written FCA names below must not fire. They used to run unconditionally,
    // so naming a chart from ANOTHER dashboard still produced "the overall cost chart" even though
    // chartLabelOf had already decided the chart doesn't exist on this page.
    const label = chartLabelOf(appContext, chart);
    if (!label) return "the chart";
    if (chart === "annual_by_category") return "the yearly breakdown chart";
    if (chart === "csp") return "the overall cost chart";
    return `the ${label}`;
};

// …and with the suffix, which keeps the yearly chart informative as its grouping changes
// ("the yearly breakdown chart (by Trade)").
export const chartDisplayName = (appContext, chart, current = {}) =>
    chart === "annual_by_category" && chartLabelOf(appContext, chart)
        ? `the yearly breakdown chart (by ${prettyValue(appContext, chart, "category", current.category || "categories")})`
        : chartBaseName(appContext, chart);

// GROUNDING: validate (chart, property, value) against the chart's LIVE options. Distinguishes WHY it
// fails so the proposal asks the right question (wrong chart vs unavailable value).
export const groundChange = (appContext, change) => {
    // A chart Otto has no name for does not exist in this context. The backend enum is deliberately wider
    // than any single page (it spans every dashboard's charts), so this is the check that keeps a request
    // naming ANOTHER page's chart from grounding here.
    const chartLabel = chartLabelOf(appContext, change && change.chart);
    if (!change || !change.chart || !chartLabel) return { ok: false, reason: "no_chart", property: change && change.property };
    if (SCALAR_PROPERTIES.has(change.property)) {
        if (!(SCALAR_SUPPORT[change.property] || []).includes(change.chart)) return { ok: false, reason: "wrong_chart", chart: change.chart, property: change.property };
        if (!isValidScalar(change.property, change.value)) return { ok: false, reason: "bad_value", chart: change.chart, property: change.property };
        return { ok: true, labels: { chartLabel, valueLabel: scalarLabel(change.property, change.value) } };
    }
    const opts = optionsFor(appContext, change.chart, change.property);
    if (!opts.length) return { ok: false, reason: "wrong_chart", chart: change.chart, property: change.property };
    const opt = opts.find(o => o.value === change.value);
    if (!opt) return { ok: false, reason: "bad_value", chart: change.chart, property: change.property };
    return { ok: true, labels: { chartLabel, valueLabel: opt.label } };
};

// Build the app_context Otto sends: current chart state (for relative commands + current-language replies)
// + the per-chart options provided by the dashboard. NO option values are invented here.
// `extra` carries the two pieces of current state NOT derivable from individualFilters alone (the dashboard
// supplies them): `maximizedChart` (which chart, if any, is currently maximized) and `cspDisplayGrouping`
// (the CSP hierarchy axis' current value, normalized to the same grouping vocabulary as the options).
export const buildChartContext = (individualFilters, chartOptions = {}, extra = {}) => {
    const f = individualFilters || {};
    // The annual chart's 4-way sortOrder (name_asc/…/value_desc) collapses to Otto's name/value concept.
    const annualSortBy = String((f.sortOrder && f.sortOrder.fca) || "").startsWith("name") ? "name" : "value";
    const { maximizedChart = null, cspDisplayGrouping = null } = extra;
    const viewOf = chart => (maximizedChart === chart ? "maximize" : "minimize");
    return {
        charts: {
            annual_by_category: {
                current: {
                    chart_type: (f.chart_view_type && f.chart_view_type.fca) || "column",
                    category: f.chart_type || "categories",
                    sort_by: annualSortBy,
                    top_n: (f.topN && f.topN.fca) || null,
                    dimension: (f.dimension && f.dimension.fca) || "2D",
                    view: viewOf("annual_by_category")
                },
                options: chartOptions.annual_by_category || {}
            },
            csp: {
                current: {
                    chart_type: (f.chart_view_type && f.chart_view_type.horizontal) || "bar",
                    // CSP groups on TWO axes at once: `category` is the breakdown (horizontal_chart_type);
                    // `display` is the hierarchy (Building/Site/Region/…), normalized to the option vocabulary.
                    category: f.horizontal_chart_type || "categories",
                    display: cspDisplayGrouping || null,
                    sort_by: f.chart_sort_by || "value",
                    top_n: (f.topN && f.topN.horizontal) || null,
                    dimension: (f.dimension && f.dimension.horizontal) || "2D",
                    view: viewOf("csp")
                },
                options: chartOptions.csp || {}
            }
        }
    };
};
